import { supabase } from '@/lib/supabase';

export interface Restaurant {
  id: string;
  nom: string;
  adresse: string | null;
  telephone: string | null;
  email: string | null;
  owner_id: string;
  plan: string | null;
}

const SELECTED_RESTAURANT_KEY = 'selected_restaurant_id';

/**
 * Service de gestion des restaurants
 */
class RestaurantService {
  /**
   * Récupérer tous les restaurants auxquels l'utilisateur a accès
   * (restaurants dont il est propriétaire + restaurants auxquels il est associé)
   */
  async getUserRestaurants(userId: string): Promise<Restaurant[]> {
    try {
      // 1. Restaurants dont l'utilisateur est propriétaire
      const { data: ownedRestaurants, error: ownedError } = await supabase
        .from('restaurants')
        .select('*')
        .eq('owner_id', userId);

      if (ownedError) {
        console.error('Erreur récupération restaurants propriétaire:', ownedError);
      }

      // 2. Restaurant associé à l'utilisateur dans la table users
      const { data: userProfile, error: profileError } = await supabase
        .from('users')
        .select('restaurant_id')
        .eq('id', userId)
        .single();

      if (profileError) {
        console.error('Erreur récupération profil:', profileError);
      }

      // Collecter tous les IDs uniques
      const restaurantIds = new Set<string>();
      const restaurants: Restaurant[] = [];

      // Ajouter les restaurants propriétés
      if (ownedRestaurants) {
        for (const r of ownedRestaurants) {
          if (!restaurantIds.has(r.id)) {
            restaurantIds.add(r.id);
            restaurants.push(r);
          }
        }
      }

      // Si l'utilisateur a un restaurant_id différent, le récupérer aussi
      if (userProfile?.restaurant_id && !restaurantIds.has(userProfile.restaurant_id)) {
        const { data: associatedRestaurant, error: assocError } = await supabase
          .from('restaurants')
          .select('*')
          .eq('id', userProfile.restaurant_id)
          .single();

        if (!assocError && associatedRestaurant) {
          restaurants.push(associatedRestaurant);
        }
      }

      return restaurants;
    } catch (error) {
      console.error('Erreur getUserRestaurants:', error);
      return [];
    }
  }

  /**
   * Récupérer un restaurant par son ID
   */
  async getRestaurant(restaurantId: string): Promise<Restaurant | null> {
    try {
      const { data, error } = await supabase
        .from('restaurants')
        .select('*')
        .eq('id', restaurantId)
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Erreur getRestaurant:', error);
      return null;
    }
  }

  /**
   * Créer un nouveau restaurant
   */
  async createRestaurant(
    userId: string,
    data: { nom: string; adresse?: string; telephone?: string; email?: string }
  ): Promise<Restaurant | null> {
    try {
      const { data: restaurant, error } = await supabase
        .from('restaurants')
        .insert({
          nom: data.nom,
          adresse: data.adresse || null,
          telephone: data.telephone || null,
          email: data.email || null,
          owner_id: userId,
          plan: 'free',
        })
        .select()
        .single();

      if (error) throw error;
      return restaurant;
    } catch (error) {
      console.error('Erreur createRestaurant:', error);
      return null;
    }
  }

  /**
   * Mettre à jour un restaurant
   */
  async updateRestaurant(
    restaurantId: string,
    data: Partial<Omit<Restaurant, 'id' | 'owner_id'>>
  ): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('restaurants')
        .update(data)
        .eq('id', restaurantId);

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Erreur updateRestaurant:', error);
      return false;
    }
  }

  /**
   * Supprimer un restaurant
   */
  async deleteRestaurant(restaurantId: string): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('restaurants')
        .delete()
        .eq('id', restaurantId);

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Erreur deleteRestaurant:', error);
      return false;
    }
  }

  /**
   * Obtenir l'ID du restaurant sélectionné depuis le localStorage
   */
  getSelectedRestaurantId(): string | null {
    try {
      return localStorage.getItem(SELECTED_RESTAURANT_KEY);
    } catch {
      return null;
    }
  }

  /**
   * Sauvegarder l'ID du restaurant sélectionné
   */
  setSelectedRestaurantId(restaurantId: string): void {
    try {
      localStorage.setItem(SELECTED_RESTAURANT_KEY, restaurantId);
    } catch (error) {
      console.error('Erreur sauvegarde restaurant sélectionné:', error);
    }
  }

  /**
   * Effacer le restaurant sélectionné
   */
  clearSelectedRestaurant(): void {
    try {
      localStorage.removeItem(SELECTED_RESTAURANT_KEY);
    } catch {
      // Ignorer les erreurs
    }
  }
}

export const restaurantService = new RestaurantService();
